import {Injectable, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ClientInfoService} from '../../ClientInfo/client-info.service';

import {ImageOverlayComponent} from '../Image/ImageOverlay/image-overlay.component';

import {BrushSettingsDialogComponent} from '../BrushSettings/Dialog/brush-settings-dialog.component';
import {ApplicationService} from '../../Application/application.service';
import {EditorControlService} from '../../../../../surface3d/src/lib/API/Public/editor-control.service';
import {DataExchangeService} from '../../../../../surface3d/src/lib/API/Public/data-exchange.service';
import {ViewComponent} from '../../../../../surface3d/src/lib/3DView/view.component';
import {Subject} from 'rxjs';
import {ConfirmationDialogComponent} from '../../Application/ConfirmationDialog/confirmation-dialog.component';
import {ImageOverlayService} from '../Image/ImageOverlay/image-overlay.service';


@Injectable({
    providedIn: 'root'
})
export class DrawViewService {

    // get/set
    private _erase = false;
    private _paint = false;
    private _selectedDegree = '2a';
    private _interactionMode: string;

    // get
    private _selectedColor: string;

    constructor(private matDialog: MatDialog,
                private clientInfoService: ClientInfoService,
                private editorControlService: EditorControlService,
                private dataExchangeService: DataExchangeService,
                private applicationService: ApplicationService,
                private imageOverlayService: ImageOverlayService) {
        this._selectedColor = '#' + this.dataExchangeService.painting.paintColor.color.getHexString();
    }

    get isLoading(): boolean {
        return this.dataExchangeService.loadingStatus >= 0 && !this.paintingLoaded;
    }

    get paintingLoaded(): boolean {
        return this.dataExchangeService.loadingStatus === 1;
    }

    get loadingStatus(): number {
        return this.dataExchangeService.loadingStatus * 100;
    }

    get pictureDrawn(): boolean {
        return this.dataExchangeService.image.drawn;
    }

    get erase(): boolean {
        return this._erase;
    }

    set erase(value: boolean) {
        this._erase = value;
    }

    get paint(): boolean {
        return this._paint;
    }

    set paint(value: boolean) {
        this._paint = value;
    }

    get selectedDegree(): string {
        return this._selectedDegree;
    }

    set selectedDegree(value: string) {
        this._selectedDegree = value;
    }

    get interactionMode(): string {
        return this._interactionMode;
    }

    set interactionMode(value: string) {
        this._interactionMode = value;
    }

    get selectedColor(): string {
        return this._selectedColor;
    }

    get databaseConnected(): boolean {
        return this.dataExchangeService.patient.id !== undefined;
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * Sets view for shape change dialog
     */
    public initializeShapeView(surfaceView: ViewComponent) {
        this.dataExchangeService.patient.shapeView = surfaceView;
    }

    /**
     * activates measuring
     * deactivates drawing
     */
    public activateMeasureMode() {
        this.paint = false;
        this.erase = false;
        this.interactionMode = 'measureMode';
        this.applicationService.measureMode = true;
        // this.applicationService.drawMode = false;
        this.editorControlService.ctrl.next('measure');

    }

    /**
     * ends measuring
     * removes distance markers
     */
    public deactivateMeasuring() {
        this.applicationService.measureMode = false;

    }

    /**
     * activates panning
     * deactivates drawing
     */
    public activatePanMode() {
        this.paint = false;
        this.erase = false;
        this.editorControlService.ctrl.next('pan');
        // this.editorControlService.event = this.editorControlService.controls.pan;
        // this.applicationService.drawMode = false;
        this.applicationService.deactivateAll();
    }

    public activateInfoMode() {
        this.interactionMode = 'infoMode';
        // this.applicationService.measureMode = false;
        // this.applicationService.drawMode = false;
        this.applicationService.infoMode = true;
        this.editorControlService.ctrl.next('info');
    }

    /**
     * toggles erase mode (erase/pan)
     */
    public changeEraseMode() {
        this.activateErasing();
    }

    /**
     * toggles paint mode (paint/pan)
     */
    public changePaintMode() {
        this.activatePainting();
    }

    /**
     * opens dialog for brush settings
     * applies settings and activates erasing/painting
     */
    public openBrushSettingsDialog() {
        const dialogRef = this.matDialog.open(BrushSettingsDialogComponent);

        const top = this.clientInfoService.smallScreenSize ? '70%' : '55%';
        const width = this.clientInfoService.smallScreenSize ? '70%' : '33.33';
        // const top = '80%';
        dialogRef.updatePosition({top: top});
        dialogRef.updateSize(width);

        dialogRef.disableClose = true;
        dialogRef.afterClosed().subscribe(result => {
            this.dataExchangeService.painting.removeBrushSize();
        });

        if (this.interactionMode === 'eraseMode') {
            this.activateErasing();
            this.paint = false;
        } else {
            this.activatePainting();
            this.erase = false;
        }

    }

    /**
     * open dialog to validate clearing of all marked areas
     * if confirmed clears them and activates control mode
     */
    public openConfirmationDialog() {

        let width = '20%';
        if (this.clientInfoService.isHandset) {
            if (this.clientInfoService.smallScreenSize) {
                width = '75%';
            } else {
                width = '50%';
            }
        }

        const dialogRef = this.matDialog.open(ConfirmationDialogComponent,
            {
                data: {
                    text: 'ClearAll',
                    cancel: true
                },
                width: width
            });
        dialogRef.disableClose = true;

        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.dataExchangeService.painting.clear();
                this.dataExchangeService.resetView();
                this.dataExchangeService.updateViewSize();
            }
            this.paint = false;
            this.erase = false;
            this.interactionMode = 'transform';
            this.applicationService.drawMode = false;
            this.editorControlService.ctrl.next('pan');
            this.dataExchangeService.patient.modelChanged = true;
        });
    }

    /**
     * opens dialog for image selection
     */
    public openImageDialog() {
        let width = '30%';
        if (this.clientInfoService.isHandset) {
            if (this.clientInfoService.smallScreenSize) {
                width = '200%';
            } else {
                width = '150%';
            }
        }
        const dialogRef = this.matDialog.open(ImageOverlayComponent, {width: width});
        dialogRef.disableClose = true;
    }

    /**
     * resets camera position to default
     * activates control mode
     */
    public resetCamera() {
        this.dataExchangeService.image.resetTransformations();
        this.dataExchangeService.resetView();

        // this.editorControlService.ctrl.next('resetTransformations');
        // this.editorControlService.event = this.editorControlService.controls.reset;
        this.paint = false;
        this.erase = false;
        this.interactionMode = 'transform';
        this.applicationService.deactivateAll();
        // this.applicationService.drawMode = false;
        this.editorControlService.ctrl.next('pan');
        // this.dataExchangeService.landmarksVisible = false;
        this.imageOverlayService.landmarksActive = false;
        this.dataExchangeService.updateViewSize();

        this.editorControlService.ctrl.next('pan');
    }

    /**
     * uploads file if it is a valid image
     */
    public uploadImage(file: File, uploader: any) {
        this.activatePanMode();

        if (file.type.match('image.*')) {
            const reader = new FileReader();
            // Read in the image file as a data URL.
            reader.readAsDataURL(file);
            reader.onload = () => {
                this.dataExchangeService.image.overlayImage = reader.result;
                uploader.value = '';
            };

        } else {
            alert('not an image');
        }
    }

    /**
     * changes paint color based on selected degree
     */
    public changePaintColor() {
        switch (this.selectedDegree) {
            case '2a':
                this.changeToColor(this.dataExchangeService.painting.colors.color2a);
                break;
            case '2b':
                this.changeToColor(this.dataExchangeService.painting.colors.color2b);
                break;
            case '3':
                this.changeToColor(this.dataExchangeService.painting.colors.color3);
                break;
            case '4':
                this.changeToColor(this.dataExchangeService.painting.colors.color4);
                break;
        }
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    private activateErasing() {
        this.erase = true;
        this.paint = false;
        this.dataExchangeService.painting.paintColor = this.dataExchangeService.painting.colors.colorSkin;
        this.editorControlService.ctrl.next('erase');
        // this.editorControlService.event = this.editorControlService.controls.erase;
        this.applicationService.drawMode = true;
    }

    private activatePainting() {
        this.paint = true;
        this.erase = false;
        this.editorControlService.ctrl.next('drawOnTextureBrush');
        // this.editorControlService.event = this.editorControlService.controls.drawOnTextureBrush;
        this.applicationService.drawMode = true;
        this.changePaintColor();

    }

    private changeToColor(color) {
        this.dataExchangeService.painting.paintColor = color;
        this._selectedColor = '#' + color.color.getHexString();
        if (this.erase) {
            this.activateErasing();
        }
    }

    public exportGltf(): void {
        this.dataExchangeService.exportGLTF();
    }
}
